import { useCallback, useEffect, useState } from 'react';
import { BookOpen, Calendar, Check, Link2, X } from 'lucide-react';
import { apiClient } from '../../lib/api-client';
import { useCacheEvents } from '../../hooks/useCacheEvents';
import { useActivityHeartbeat } from '../../hooks/useActivityHeartbeat';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader } from '../ui/Card';
import { Skeleton } from '../ui/Skeleton';
import { cn } from '../../lib/utils';

export interface KnowledgeEntry {
  id: string;
  type: string;
  content: string;
  confidence?: number;
  status: string;
  source?: string;
  learnedAt?: string;
}

function normalizeKnowledgeEntry(raw: Record<string, unknown>): KnowledgeEntry {
  return {
    id: String(raw.id ?? raw.entryId ?? ''),
    type: String(raw.type ?? raw.entryType ?? 'note'),
    content: String(raw.content ?? raw.text ?? ''),
    confidence:
      typeof raw.confidence === 'number'
        ? raw.confidence
        : typeof raw.confidenceScore === 'number'
          ? (raw.confidenceScore as number)
          : undefined,
    status: String(raw.status ?? 'pending').toLowerCase(),
    source: raw.source != null ? String(raw.source) : raw.sourceLabel != null ? String(raw.sourceLabel) : undefined,
    learnedAt:
      raw.learnedAt != null
        ? String(raw.learnedAt)
        : raw.createdAt != null
          ? String(raw.createdAt)
          : raw.timestamp != null
            ? String(raw.timestamp)
            : undefined,
  };
}

function formatWhen(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function previewText(text: string, max = 220): string {
  const t = text.replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max)}…`;
}

function confidenceBadge(confidence?: number) {
  if (confidence == null || Number.isNaN(confidence)) {
    return <Badge variant="outline">n/a</Badge>;
  }
  const pct = confidence <= 1 ? Math.round(confidence * 100) : Math.round(confidence);
  if (pct >= 85) {
    return <Badge variant="success">{pct}%</Badge>;
  }
  if (pct < 40) {
    return <Badge variant="destructive">{pct}%</Badge>;
  }
  return (
    <Badge
      variant="secondary"
      className="bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200"
    >
      {pct}%
    </Badge>
  );
}

function statusBadge(status: string) {
  const s = status.toLowerCase();
  if (s === 'approved') return <Badge variant="success">approved</Badge>;
  if (s === 'rejected') return <Badge variant="destructive">rejected</Badge>;
  if (s === 'pending') {
    return (
      <Badge
        variant="outline"
        className="border-amber-300 text-amber-800 dark:border-amber-700 dark:text-amber-200"
      >
        pending
      </Badge>
    );
  }
  return <Badge variant="outline">{status}</Badge>;
}

export interface LearnedEntriesTabProps {
  streamId: string;
  canManage: boolean;
  onTotalChange?: (total: number) => void;
}

export function LearnedEntriesTab({ streamId, canManage, onTotalChange }: LearnedEntriesTabProps) {
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useActivityHeartbeat('cache:ai-learning', Boolean(streamId));

  const load = useCallback(async () => {
    if (!streamId) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiClient.getAiWorkflowKnowledge(streamId);
      const list = Array.isArray(res.entries) ? res.entries.map((e) => normalizeKnowledgeEntry(e as Record<string, unknown>)) : [];
      setEntries(list);
      onTotalChange?.(typeof res.total === 'number' ? res.total : list.length);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load knowledge');
      setEntries([]);
      onTotalChange?.(0);
    } finally {
      setIsLoading(false);
    }
  }, [streamId, onTotalChange]);

  useEffect(() => {
    void load();
  }, [load]);

  useCacheEvents({
    enabled: Boolean(streamId),
    cacheNames: ['ai-learning'],
    onEvent: () => {
      void load();
    },
  });

  const handleDecision = async (entryId: string, status: 'approved' | 'rejected') => {
    if (!entryId) return;
    setBusyId(entryId);
    try {
      await apiClient.updateAiWorkflowKnowledgeEntry(streamId, entryId, { status });
      setError(null);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-36 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 dark:border-red-900/40">
        <CardContent className="py-6 text-sm text-red-700 dark:text-red-300">{error}</CardContent>
      </Card>
    );
  }

  if (entries.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-500 dark:text-gray-400">
          <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-60" />
          <p>No learned entries for this stream yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {entries.map((entry) => {
        const showActions =
          canManage && entry.id && ['pending', 'draft', 'review'].includes(entry.status.toLowerCase());
        return (
          <Card
            key={entry.id || entry.content.slice(0, 24)}
            className="border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2 min-w-0">
                  <Badge variant="secondary" className="capitalize">
                    {entry.type}
                  </Badge>
                  {confidenceBadge(entry.confidence)}
                  {statusBadge(entry.status)}
                  {entry.id && (
                    <span className="text-xs font-mono text-gray-400 dark:text-gray-500 truncate max-w-[200px]" title={entry.id}>
                      {entry.id}
                    </span>
                  )}
                </div>
                {showActions && (
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-green-700 border-green-300 hover:bg-green-50 dark:text-green-300 dark:border-green-700 dark:hover:bg-green-950/40"
                      disabled={busyId === entry.id}
                      onClick={() => handleDecision(entry.id, 'approved')}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-700 border-red-300 hover:bg-red-50 dark:text-red-300 dark:border-red-800 dark:hover:bg-red-950/40"
                      disabled={busyId === entry.id}
                      onClick={() => handleDecision(entry.id, 'rejected')}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
              <p className={cn('leading-relaxed', 'whitespace-pre-wrap break-words')}>{previewText(entry.content)}</p>
              <div className="flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
                {entry.source && (
                  <span className="inline-flex items-center gap-1 min-w-0">
                    <Link2 className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate max-w-[280px]" title={entry.source}>
                      {entry.source}
                    </span>
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  Learned {formatWhen(entry.learnedAt)}
                </span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
